import { Component, Inject, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption } from "@vengreso/angular-components";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { distinctUntilChanged } from "rxjs/operators";

import { Router } from "@angular/router";
import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";
import {
  PlansConstants,
  RewardsConstants,
  getPlanId,
  getRewards,
} from "src/app/core/core-services/constants/plans.constants";
import { TinyMceConfigService } from "src/app/core/core-services/services/tiny-mce-config.service";
import { Store } from "@ngrx/store";
import { selectPlanFeatures } from "src/app/core/store/plan/plan.selectors";
import { PlanFeatures } from "src/app/core/models/stripe.models";
import { Subscription } from "rxjs";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { ShortcutService } from "src/app/core/core-services/services/shortcut.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { CategoryRenameComponent } from "../add-shortcut-category/category-rename/category-rename.component";
import { CategorySelectComponent } from "../../shared/category-select/category-select.component";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { DynamicStorage } from "src/app/core/core-services/decorators/dynamic-storage";
import { clearTinyMCEContent } from "src/app/utils/getSortcutContent";
@Component({
    selector: "app-duplicate-shortcut",
    templateUrl: "./duplicate-shortcut.component.html",
    styleUrls: ["./duplicate-shortcut.component.scss"],
    standalone: false
})
export class DuplicateShortcutComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  @DynamicStorage(SHORTCUT_CATEGORIES) categoryList: any[];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true"
  // (same recipe as add-shortcut.component.ts).
  categoryOptions: VngSelectOption[] = [];
  editorConfig: any = null; // Initialize as null to prevent undefined errors
  editorApiKey: string;
  editorManager: any;
  isEditorLoaded: boolean;
  @ViewChild("categorySelect") categorySelect: CategorySelectComponent;
  private subscriptions = new Subscription();
  currentFeatures: PlanFeatures;
  currentPlanId: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    config: NgbPopoverConfig,
    private readonly dataService: DataService,
    private readonly shortcutService: ShortcutService,
    private readonly notification: NotificationService,
    private readonly extensionService: ExtensionService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<DuplicateShortcutComponent>,
    public dialog: DialogService,
    private readonly sharedData: SharedDataService,
    private readonly shortcutCategoryService: ShortcutCategoryService,
    private readonly tinyMceConfigService: TinyMceConfigService,
    private readonly store: Store
  ) {
    config.triggers = "hover";
    config.container = "body";
  }

  async ngOnInit() {
    this.editorApiKey = ApplicationConstants.TINYMCE_API_KEY;
    this.currentPlanId = getPlanId();

    this.subscriptions.add(
      this.store.select(selectPlanFeatures).subscribe((features: PlanFeatures) => {
        if (features) {
          this.currentFeatures = features;
          this.updateEditorConfig();
        }
      })
    );

    this.form = this.fb.group({
      title: [this.data?.data.title, [Validators.required]],
      shortcut: [null, [Validators.required]],
      category_id: [this.data?.data.category_id, [Validators.required]],
      uploads: [this.data?.data.uploads],
      html: [this.data?.data.html, [Validators.required]],
    });

    this.getCategories();

    this.sharedData.storage
      .observe(SHORTCUT_CATEGORIES)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((value) => {
        this.categoryList = value;
        this.setCategory();
      });

    this.setCategory();
  }

  private updateEditorConfig() {
    const baseConfig = this.shortcutService.getEditorConfig();
    this.editorConfig = this.tinyMceConfigService.getPlanBasedConfig(
      this.currentFeatures,
      baseConfig,
      this,
      this.currentPlanId,
      (type, error, showTheStarter) => {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { type, error, showTheStarter },
          "500px"
        );
      }
    );
  }

  flycutExistsSearch(value: any): any {
    this.dataService.flycutExistsSearch({ q: value }).subscribe((response: any) => {
      if (response && response.result) {
        if (response.result.data.exists) {
          this.form.controls.shortcut.setErrors({ notUnique: true });
        }
      }
    });
  }

  // R5: flatten the category tree into data-driven vng-select options once
  // per categoryList change (same recipe as add-shortcut.component.ts).
  buildCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.categoryList || []) {
      options.push({ value: cat.id, label: cat.name });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name });
        for (const sub2 of sub1.sub_category_lv2 || []) {
          options.push({ value: sub2.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + sub2.name });
        }
      }
    }
    this.categoryOptions = options;
  }

  saveCategory(data) {
    this.dataService.post({ url: "v1/user/shortcut-category", data }).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.getCategories(true);
          this.extensionService.sendUpdateMessage();
          this.data.self.categoryId = response.result;
          this.categorySelect.closePanel();
          this.notification.toast("Category added successfully!");
        }
      },
      () => { }
    );
  }

  shortcutCategoryRename(data: any) {
    this.dialog.openDialogComponent(CategoryRenameComponent, {
      self: this,
      data,
      backdropClass: "dialog-category-backdrop",
    });
  }

  setEditorManager(event: any) {
    this.isEditorLoaded = true;
    this.editorManager = event.editor.editorManager;
  }

  setCategory(): void {
    this.buildCategoryOptions();
    this.form.patchValue({
      category_id:
        this.data.self.categoryId && this.data.self.categoryId !== "all"
          ? this.data.self.categoryId
          : this.categoryList[0].id,
    });
  }

  getCategories(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }

  getCategoryType(id: string) {
    for (const item of this.categoryList) {
      if (item.id == id) {
        return "category";
      }
      if (item.sub_category_lv1) {
        for (const subLv1 of item.sub_category_lv1) {
          if (subLv1.id == id) {
            return "sub_categories_lv1";
          }
          if (subLv1.sub_category_lv2) {
            for (const subLv2 of subLv1.sub_category_lv2) {
              if (subLv2.id == id) {
                return "sub_categories_lv2";
              }
            }
          }
        }
      }
    }
  }

  saveShortcut(data: any, editorId: string) {
    const editor = this.editorManager.get(editorId);
    this.shortcutService.processMedia(editor, (uploads: string[]) => {
      const shortcut: any = this.dataService.toSave(data, ["title", "shortcut", "category_id"]);
      shortcut.uploads = uploads;
      shortcut.html = clearTinyMCEContent(editor.getContent());
      shortcut.text = this.shortcutService.getInlineStyledHTML(shortcut.html);
      shortcut.first_line = this.shortcutService.getFirstLine(editor);
      shortcut.type = this.getCategoryType(data.category_id);

      const rewards = getRewards();
      const isValid = this.tinyMceConfigService.validateShortcutContent(
        shortcut.html,
        shortcut.text,
        this.currentFeatures,
        this.currentPlanId,
        rewards
      );

      if (!isValid) {
        const isFree = this.currentPlanId === "Free";
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          {
            self: this,
            type: "numOfChar",
            showTheStarter: isFree,
            error: isFree ? "maxCharFree" : "maxCharStarter"
          },
          "800px"
        );
        return;
      }

      this.dataService.post({ url: "v1/user/shortcut", data: shortcut }).subscribe((response: any) => {
        if (response && response.result) {
          this.extensionService.sendUpdateMessage();
          this.close();
          if (this.data.isDashboard) {
            this.data.self.getShortcutList();
          } else {
            this.data.self.listService.getList(true);
          }
          this.sharedData.setAttribute("expand", response.result);
          this.router.navigate(["/shortcuts", data.category_id]);
        }
      });
    });
  }

  checkForError() {
    // R3: field errors are only painted once a control is dirty/touched, so
    // the submit path has to mark the whole form as touched.
    this.form.markAllAsTouched();

    if (this.form.controls["shortcut"].hasError("required")) {
      this.notification.toast("FlyCut is required", "OK", 6000);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
